import {documentToReactComponents} from '@contentful/rich-text-react-renderer';
import {Theme, Typography} from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import {makeStyles} from '@material-ui/core/styles';
import React, {FunctionComponent, useEffect, useState} from 'react';
import {WithTranslation} from 'react-i18next';
import {DocumentBreakpointWrapper} from '../../../components/DocumentModal/DocumentBreakpointWrapper';
import DocumentModal from '../../../components/DocumentModal/DocumentModal';
import {GenericInput} from '../../../components/Form/GenericInput/GenericInput';
import {FormAnnotation} from '../../../components/FormAnnotation/FormAnnotation';
import NavigationBar from '../../../components/NavigationBar';
import {PageTitle} from '../../../components/PageTitle/PageTitle';
import {useUniversalTracking} from '../../../lib/tracking/universalTracking';
import {ContractState, Produkt, SalesType} from '../../../types/redux';
import {ContentfulState} from '../../../redux/reducers/contentful/ContentfulState';
import {onPressEnterHandlerFactory} from '../../../utils/formInteraction';

const emailValidator = require('email-validator');

const useStyles = makeStyles((theme: Theme) => ({
	sectionTitle: {
		marginTop: theme.spacing(3),
		marginBottom: theme.spacing(2),
	},
	inputWrapper: {
		marginBottom: theme.spacing(3),
	},
}));

interface Props extends WithTranslation, StateProps, DispatchProps {
	onSubmit(data: any): void;
}

const Contact: FunctionComponent<Props> = (props) => {
	const classes = useStyles();
	const [email, setEmail] = useState(props.contract.email);
	const [emailError, setEmailError] = useState(undefined);
	const [phone, setPhone] = useState(props.contract.telefonnummer);
	const [phoneError, setPhoneError] = useState(undefined);
	const [showGDPRModal, setShowGDPRModal] = useState(false);
	const { trackEvent } = useUniversalTracking();
	const phoneRegexPattern = /[^0-9-+/. ]/gi;
	const { contentful, contract } = props;
	const gdprDefaultMobileDoc = contentful.GDPR_mobile ? documentToReactComponents(contentful.GDPR_mobile) : null; //keh
	const gdprDefaultDesktopDoc = contentful.GDPR?.fields?.file?.url;		//keh
	const gdprKppMobileDoc = contentful.GDPR_kpp_mobile ? documentToReactComponents(contentful.GDPR_kpp_mobile) : null;
	const gdprKppDesktopDoc = contentful.GDPR_kpp?.fields?.file?.url;
	const gdprKpeMobileDoc = contentful.GDPR_kpe_mobile ? documentToReactComponents(contentful.GDPR_kpe_mobile) : null;
	const gdprKpeDesktopDoc = contentful.GDPR_kpe?.fields?.file?.url;
	const gdprEp  = contentful.gdpr_ep?.fields?.file?.url;
	const gdprEpp = contentful.gdpr_epp?.fields?.file?.url;

	const emailRef = React.useRef<HTMLElement>(null);
	const phoneRef = React.useRef<HTMLElement>(null);
	const formRefs = [emailRef, phoneRef];

	const getGdprSrc = (isMobile: boolean) => {
		switch (contract.produkt) {
			case Produkt.EP:
				return gdprEp;
			case Produkt.EPP:
				return gdprEpp;
		}
		switch (contract.salesType) {
			case SalesType.KPP:
				return isMobile ? gdprKppMobileDoc : gdprKppDesktopDoc;
			case SalesType.KPE:
				return isMobile ? gdprKpeMobileDoc : gdprKpeDesktopDoc;
			default:
				return isMobile ? gdprDefaultMobileDoc : gdprDefaultDesktopDoc;
		}
	};

	const validateEmail = (
		value: string,
		valueError: string | undefined,
		setValueError: any,
		forceValidation: boolean = false
	) => {
		// Note: valueError === undefined will stop the validation from happening, we will start
		// validating as soon as length > 0 or an external function sets valueError !== undefined
		if (value) {
			setValueError('');
		}

		if (forceValidation || valueError !== undefined) {
			const isValid = emailValidator.validate(email);
			setValueError(isValid ? '' : props.t('contact.not_valid_email'));
		}
	};

	const validatePhone = (
		value: string,
		valueError: string | undefined,
		setValueError: any,
		forceValidation: boolean = false
	) => {
		// Note: valueError === undefined will stop the validation from happening, we will start
		// validating as soon as length > 0 or an external function sets valueError !== undefined
		if (value) {
			setValueError('');
		}

		if (forceValidation || valueError !== undefined) {
			const isValid = !phoneRegexPattern.test(value);
			setValueError(isValid ? '' : props.t('contact.not_valid_phone'));
		}
	};

	useEffect(
		() => validateEmail(email, emailError, setEmailError),
		// eslint-disable-next-line
		[email]
	);

	useEffect(
		() => validatePhone(phone, phoneError, setPhoneError),
		// eslint-disable-next-line
		[phone]
	);

	const formIsValid = () => {
		const errors = [emailError, phoneError].filter(
			(errMsg) => errMsg && errMsg !== ''
		);

		return errors.length === 0;
	};

	const validateAndSubmit = () => {
		// Force validations
		validateEmail(email, emailError, setEmailError, true);
		validatePhone(phone, phoneError, setPhoneError, true);

		if (formIsValid()) {
			onSubmit();
		}
	};

	const onSubmit = () => {
		trackEvent({
			category: 'contact-submit',
			action: 'click-event',
			label: 'contact',
		});
		props.onSubmit({
			email,
			telefonnummer: phone,
		});
	};

	const onGDPRClick = (event: React.MouseEvent) => {
		event.preventDefault();
		trackEvent({
			category: 'contact-open-document',
			action: 'click-event',
			label: 'contact Datenschutz',
		});
		setShowGDPRModal(true);
	};

	// event listener on Enter key press events to focus next input or submit form
	const onKeyPress = onPressEnterHandlerFactory(formRefs, validateAndSubmit);

	return (
		<div>
			<PageTitle title={props.t('contact.title')} />
			<Grid container spacing={3}>
				<Grid item xs={12} sm={9} md={6}>
					<div className={classes.inputWrapper}>
						<GenericInput
							inputId="contact_email"
							label={props.t('contact.email_label')}
							error={emailError}
							setInputValue={setEmail}
							inputValue={email}
							type="email"
							additionalInputParams={{
								inputRef: emailRef,
								onKeyPress,
								autoFocus: true,
							}}
						/>
					</div>
					<div className={classes.inputWrapper}>
						<GenericInput
							inputId="contact_phone"
							label={props.t('contact.phone_label')}
							error={phoneError}
							setInputValue={setPhone}
							inputValue={phone}
							type="tel"
							additionalInputParams={{
								inputRef: phoneRef,
								onKeyPress,
							}}
						/>
					</div>
				</Grid>
				<Grid item xs={12} sm={9} md={6}>
					<FormAnnotation>
						<Typography variant="body1">
							{props.t('contact.dsgvo_link1')}
							<button onClick={onGDPRClick}>
								<b>{props.t('contact.dsgvo_clickable_link')}</b>
							</button>
							{props.t('contact.dsgvo_link2')}
						</Typography>
					</FormAnnotation>
				</Grid>
			</Grid>
			{!showGDPRModal && <NavigationBar onClickContinue={validateAndSubmit} />}
			<DocumentModal
				showModal={showGDPRModal}
				handleCloseFn={() => setShowGDPRModal(false)}
			>
				<DocumentBreakpointWrapper
					mobileDocument={getGdprSrc(true)}
					desktopIframeSrc={getGdprSrc(false)}
					title="Datenschutzerklärung"
				/>
			</DocumentModal>
		</div>
	);
};

interface StateProps {
	contentful: ContentfulState;
	contract: ContractState;
}

interface DispatchProps {
	fetchContent(): void;
}

export default Contact;
